<template>
  <div>
    <div class="dependents-toolbar">
      <a-button @click="groupedTableRef?.expandAll()">Expandir todo</a-button>
      <a-button @click="groupedTableRef?.collapseAll()">Contraer todo</a-button>
    </div>
    <grouped-table
      ref="groupedTableRef"
      :groups="data"
      group-row-key="key"
      :group-columns="dependentColumns"
      items-key="coverages"
      item-row-key="key"
      :item-columns="coverageColumns"
      :item-slot-names="itemSlotNames"
      :detail-title="(group) => `Coberturas de ${group.fullName}`"
      default-expanded="none"
      :loading="loading"
      :page-size="10"
    >
      <template #policies="{ record }">
        <span class="summary">
          <span class="summary-count">{{ record.policyNumbers.length }}</span>
          <span class="summary-detail">
            {{ record.policyNumbers.join(' · ') }}
          </span>
        </span>
      </template>

      <template #coverages="{ record }">
        <span class="summary">
          <span class="summary-count">{{ record.coverageCount }}</span>
          <span class="summary-detail">{{ coverageSummary(record) }}</span>
        </span>
      </template>

      <template #status="{ record }">
        <a-tag :color="getColorByState(record?.status)">
          {{ (getTagText(record?.status) || '-').toUpperCase() }}
        </a-tag>
      </template>

      <template #groupAction="{ record }">
        <div class="action-cell">
          <a-tooltip
            v-if="record.dependentId"
            color="#05045D"
            title="Editar carga"
          >
            <a-button type="link" @click="$emit('edit-dependent', record)">
              <template #icon>
                <edit-outlined class="blue-icon" />
              </template>
            </a-button>
          </a-tooltip>
        </div>
      </template>

      <template #itemId="{ text }">
        <p># {{ text }}</p>
      </template>

      <template #itemStatus="{ record }">
        <a-tag :color="getColorByState(record?.status)">
          {{ (getTagText(record?.status) || '-').toUpperCase() }}
        </a-tag>
      </template>

      <template #itemAction="{ record }">
        <div class="action-cell">
          <a-col>
            <a-tooltip color="#05045D" title="Editar vigencia">
              <a-button type="link" @click="$emit('edit-dates', record)">
                <template #icon>
                  <edit-outlined class="blue-icon" />
                </template>
              </a-button>
            </a-tooltip>
          </a-col>
          <a-col>
            <a-tooltip color="#05045D" title="Dar de baja asociación">
              <a-button type="link" @click="$emit('unsubscribe', record)">
                <template #icon>
                  <download-outlined />
                </template>
              </a-button>
            </a-tooltip>
          </a-col>
          <a-col>
            <a-tooltip
              color="#05045D"
              title="Quitar asociación carga-cobertura"
            >
              <a-button type="link" @click="$emit('remove', record)">
                <template #icon>
                  <delete-outlined class="red-icon" />
                </template>
              </a-button>
            </a-tooltip>
          </a-col>
        </div>
      </template>
    </grouped-table>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref } from 'vue';
import {
  EditOutlined,
  DownloadOutlined,
  DeleteOutlined
} from '@ant-design/icons-vue';
import GroupedTable from '@/components/tables/grouped-table.vue';
import getTagColor from '@/utils/getTagColor';
import getTagText from '@/utils/getTagText';

defineProps({
  // Dependents grouped by dependent (see utils/stockDependentGroups.js).
  data: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  }
});

defineEmits(['edit-dependent', 'edit-dates', 'unsubscribe', 'remove']);

const groupedTableRef = ref();

const itemSlotNames = ['itemId', 'itemStatus', 'itemAction'];

const dependentColumns = [
  {
    title: 'RUT',
    dataIndex: 'rut',
    key: 'rut',
    width: 150
  },
  {
    title: 'Nombre completo',
    dataIndex: 'fullName',
    key: 'fullName',
    width: 240
  },
  {
    title: 'Fecha de nacimiento',
    dataIndex: 'birthdayDate',
    key: 'birthdayDate',
    width: 170
  },
  {
    title: 'Relación',
    dataIndex: 'relationshipLabel',
    key: 'relationshipLabel',
    width: 140
  },
  {
    title: 'Pólizas',
    key: 'policies',
    width: 260,
    slots: { customRender: 'policies' }
  },
  {
    title: 'Coberturas',
    key: 'coverages',
    width: 230,
    slots: { customRender: 'coverages' }
  },
  {
    title: 'Estado',
    dataIndex: 'status',
    key: 'status',
    width: 130,
    slots: { customRender: 'status' }
  },
  {
    title: 'Acción',
    key: 'action',
    width: 100,
    fixed: 'right',
    align: 'center',
    slots: { customRender: 'groupAction' }
  }
];

const coverageColumns = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: 110,
    slots: { customRender: 'itemId' }
  },
  {
    title: 'N° Póliza',
    dataIndex: 'policyNumber',
    key: 'policyNumber',
    width: 140
  },
  {
    title: 'Grupo',
    dataIndex: 'groupName',
    key: 'groupName',
    width: 230
  },
  {
    title: 'Cobertura',
    dataIndex: 'coverageName',
    key: 'coverageName',
    width: 230
  },
  {
    title: 'Vigencia',
    dataIndex: 'vigencia',
    key: 'vigencia',
    width: 260
  },
  {
    title: 'Estado',
    dataIndex: 'status',
    key: 'status',
    width: 130,
    slots: { customRender: 'itemStatus' }
  },
  {
    title: 'Acción',
    key: 'action',
    width: 180,
    align: 'center',
    slots: { customRender: 'itemAction' }
  }
];

const coverageSummary = (group) => {
  const parts = [];
  if (group.activeCount) {
    parts.push(
      `${group.activeCount} ${group.activeCount === 1 ? 'vigente' : 'vigentes'}`
    );
  }
  if (group.inactiveCount) {
    parts.push(
      `${group.inactiveCount} no ${
        group.inactiveCount === 1 ? 'vigente' : 'vigentes'
      }`
    );
  }
  return parts.join(' · ');
};

const getColorByState = (value) => getTagColor(value);
</script>

<style scoped>
.dependents-toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 12px;
}
.summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.summary-count {
  display: inline-grid;
  place-items: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 11px;
  background: #eeeafe;
  color: #231a8c;
  font-size: 12px;
  font-weight: 600;
}
.summary-detail {
  color: #8b939b;
  font-size: 13px;
}
.action-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
.blue-icon {
  color: #05045d;
}
.red-icon {
  color: #ff4d4f;
}
</style>
